import { ReactNode, useEffect, useState } from 'react';
import { Alert, Button, Card, Input, Modal, Space, Spin, Typography } from 'antd';
import { DeleteOutlined, LockOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';
import {
  getAuthStatus,
  getAuthenticationOptions,
  getRegistrationOptions,
  logout,
  resetAuth,
  verifyAuthentication,
  verifyRegistration,
} from '../services/auth';
import './PasskeyGate.css';

type GateState = 'checking' | 'needsRegistration' | 'needsLogin' | 'ready';

const supportsPasskeys = () =>
  typeof window !== 'undefined' && typeof PublicKeyCredential !== 'undefined';

export function PasskeyGate({ children }: { children: ReactNode }) {
  const [state, setState] = useState<GateState>('checking');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userName, setUserName] = useState<string | undefined>();
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetSecret, setResetSecret] = useState('');
  const [resetLoading, setResetLoading] = useState(false);

  const refreshStatus = async () => {
    setLoading(true);
    setError(null);
    try {
      const status = await getAuthStatus();
      setUserName(status.userName);
      const hasCreds = status.hasCredentials ?? status.hasUser;
      if (status.authenticated) {
        setState('ready');
      } else if (hasCreds) {
        setState('needsLogin');
      } else {
        setState('needsRegistration');
      }
    } catch (err) {
      console.error(err);
      setError('Unable to check authentication status.');
      setState('needsLogin');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refreshStatus();
  }, []);

  const handleRegister = async () => {
    if (!supportsPasskeys()) {
      setError('Passkeys are not supported on this device/browser.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const options = await getRegistrationOptions();
      const credential = await startRegistration({ optionsJSON: options });
      await verifyRegistration(credential);
      await refreshStatus();
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        setError('Registration was cancelled.');
      } else if (err?.name === 'InvalidStateError') {
        setError('This device already has this passkey. Try signing in.');
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('Failed to register passkey.');
      }
      await refreshStatus();
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!supportsPasskeys()) {
      setError('Passkeys are not supported on this device/browser.');
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const options = await getAuthenticationOptions();
      const assertion = await startAuthentication({ optionsJSON: options });
      await verifyAuthentication(assertion);
      await refreshStatus();
    } catch (err: any) {
      if (err?.name === 'AbortError') {
        setError('Authentication was cancelled.');
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('Failed to authenticate with passkey.');
      }
      await refreshStatus();
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error(err);
    } finally {
      await refreshStatus();
    }
  };

  const handleReset = async () => {
    if (!resetSecret.trim()) {
      setError('Please enter the reset secret');
      return;
    }

    setResetLoading(true);
    setError(null);
    try {
      await resetAuth(resetSecret.trim());
      setShowResetModal(false);
      setResetSecret('');
      await refreshStatus();
    } catch (err: any) {
      setError(err?.message || 'Reset failed. Check your secret and try again.');
    } finally {
      setResetLoading(false);
    }
  };

  if (state === 'ready') {
    return (
      <>
        {/* <button className="passkey-signout" onClick={handleLogout} type="button">
          Sign out
        </button> */}
        {children}
      </>
    );
  }

  return (
    <div className="passkey-overlay">
      <Card className="passkey-card" bordered={false}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div className="passkey-header">
            <SafetyCertificateOutlined className="passkey-icon" />
            <div>
              <Typography.Title level={3} style={{ margin: 0 }}>
                Passkey Required
              </Typography.Title>
              <Typography.Text className="passkey-hint">
                Unlock this tracker with your Apple passkey. Your passkey stays on your devices;
                nothing is shared.
              </Typography.Text>
            </div>
          </div>

          {state === 'checking' && (
            <div className="passkey-center">
              <Spin size="large" />
            </div>
          )}

          {error && <Alert type="error" message={error} showIcon />}

          {supportsPasskeys() ? (
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {state === 'needsRegistration' && (
                <Typography.Text className="passkey-body">
                  No passkey is registered yet. Use Face ID/Touch ID on your Apple device to set one
                  up now.
                </Typography.Text>
              )}
              {state === 'needsLogin' && (
                <Typography.Text className="passkey-body">
                  {userName ? `Welcome back, ${userName}.` : 'Welcome back.'} Confirm with your
                  passkey to continue.
                </Typography.Text>
              )}

              <div className="passkey-actions">
                {state === 'needsRegistration' && (
                  <Button
                    type="primary"
                    size="large"
                    block
                    icon={<SafetyCertificateOutlined />}
                    onClick={handleRegister}
                    loading={loading}
                  >
                    Create a passkey
                  </Button>
                )}
                {state === 'needsLogin' && (
                  <Button
                    type="primary"
                    size="large"
                    block
                    icon={<LockOutlined />}
                    onClick={handleLogin}
                    loading={loading}
                  >
                    Unlock with passkey
                  </Button>
                )}
                <Button
                  icon={<ReloadOutlined />}
                  size="large"
                  block
                  onClick={refreshStatus}
                  disabled={loading}
                >
                  Refresh status
                </Button>
                {state === 'needsLogin' && (
                  <Button
                    icon={<DeleteOutlined />}
                    size="large"
                    block
                    danger
                    onClick={() => setShowResetModal(true)}
                    disabled={loading}
                  >
                    Reset & Start Over
                  </Button>
                )}
              </div>
            </Space>
          ) : (
            <Alert
              type="warning"
              message="This browser does not support passkeys. Use Safari on iOS/iPadOS/macOS with Face ID or Touch ID."
              showIcon
            />
          )}
        </Space>
      </Card>

      <Modal
        title="Reset Authentication"
        open={showResetModal}
        onCancel={() => {
          setShowResetModal(false);
          setResetSecret('');
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setShowResetModal(false);
            setResetSecret('');
          }}>
            Cancel
          </Button>,
          <Button
            key="reset"
            type="primary"
            danger
            loading={resetLoading}
            onClick={handleReset}
          >
            Reset Everything
          </Button>,
        ]}
      >
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Typography.Text>
            This will clear all passkey data and let you register a new passkey.
            Enter your reset secret to confirm.
          </Typography.Text>
          <Input.Password
            placeholder="Enter reset secret"
            value={resetSecret}
            onChange={(e) => setResetSecret(e.target.value)}
            onPressEnter={handleReset}
          />
        </Space>
      </Modal>
    </div>
  );
}
